type Product = {
  id: string
  parentProduct?: string
  organizationProductId?: string
  organizationComboId?: string
  categoryId?: string
}

type DiscountedProduct = Product & {
  finalPrice: number
  discountType?: '2x1' | 'percentage' | 'currency' | 'cash' | null
  discountAmount?: number | null
  discountConcept?: string | null
  promotionId?: string
  quantity: number
  fullPrice: number
}

type Promotion = {
  discountType: '2x1' | 'percentage' | 'currency'
  products?: string[]
  categories?: string[]
  name?: string
  promotionId?: string
  discountAmount?: number
  allowRepeat?: boolean
}

export default class PromotionProductsApplier {
  availablePromotions: Promotion[]
  products: Record<string, Product>

  constructor(availablePromotions: Promotion[], products: Record<string, Product>) {
    this.availablePromotions = availablePromotions
    this.products = products
  }

  updateDiscountInProducts(products: DiscountedProduct[]): DiscountedProduct[] {
    const productsAvailable = this.filterProductsWithManualPromotions(products)
    const productsWithoutDiscount = this.removeDiscountInProducts(productsAvailable)
    const productsWithNewDiscount = this.addDiscountInProducts(productsWithoutDiscount)
    const productsChanged = this.productsDiscountDiff(products, productsWithNewDiscount)
    return productsChanged
  }

  filterProductsWithManualPromotions(products: DiscountedProduct[]): DiscountedProduct[] {
    return products.filter(product => product.promotionId || !product.discountType)
  }

  removeDiscountInProducts(products: DiscountedProduct[]): DiscountedProduct[] {
    return products.map(product => ({
      ...product,
      finalPrice: product.fullPrice,
      discountType: null,
      discountAmount: product.discountAmount === 0 ? 0 : null,
      discountConcept: null
    }))
  }

  addDiscountInProducts(products: DiscountedProduct[]): DiscountedProduct[] {
    let productsWithNewDiscount: DiscountedProduct[] = []
    let productsPendingToDiscount: DiscountedProduct[] = products
    const catalogProducts = this.products
    this.availablePromotions
      .filter(promotion => {
        return ['2x1', 'percentage'].includes(promotion.discountType)
      })
      .forEach(promotion => {
        const promotionProducts = productsPendingToDiscount.filter(product => {
          let parentProduct = catalogProducts[product.parentProduct!]
          if (!parentProduct) {
            parentProduct = catalogProducts[product.id]
          }
          if (!parentProduct || product.finalPrice === 0) return false
          if (promotion.products) {
            return promotion.products.includes(
              parentProduct.organizationProductId || parentProduct.organizationComboId!
            )
          } else if (promotion.categories) {
            return promotion.categories.includes(parentProduct.categoryId!)
          }
        })

        const promotionInstance = PromotionFactory.getPromotion(promotion)
        const productsWithPromotionApplied =
          promotionInstance.applyPromotionIntoProducts(promotionProducts)
        const productsWithPromotionAppliedIds = productsWithPromotionApplied.map(
          product => product.id
        )
        productsPendingToDiscount = productsPendingToDiscount.filter(
          product => !productsWithPromotionAppliedIds.includes(product.id)
        )
        productsWithNewDiscount = productsWithNewDiscount.concat(productsWithPromotionApplied)
      })
    return productsWithNewDiscount.concat(productsPendingToDiscount)
  }

  productsDiscountDiff(
    oldProducts: DiscountedProduct[],
    newProducts: DiscountedProduct[]
  ): DiscountedProduct[] {
    const oldProductsMap: Record<string, DiscountedProduct> = oldProducts.reduce(
      (acc, product) => {
        acc[product.id] = product
        return acc
      },
      {} as Record<string, DiscountedProduct>
    )
    return newProducts.filter(newProduct => {
      const oldProduct = oldProductsMap[newProduct.id]
      return (
        oldProduct.discountType !== newProduct.discountType ||
        oldProduct.discountAmount !== newProduct.discountAmount
      )
    })
  }
}

class PromotionFactory {
  static getPromotion(promotion: Promotion): Promotion2x1 | PromotionPercentage {
    switch (promotion.discountType) {
      case '2x1':
        return new Promotion2x1(promotion)
      case 'percentage':
        return new PromotionPercentage(promotion)
      default:
        throw new Error('Unknown promotion type')
    }
  }
}

class Promotion2x1 {
  promotion: Promotion

  constructor(promotion: Promotion) {
    this.promotion = promotion
  }

  applyPromotionIntoProducts(promotionProducts: DiscountedProduct[]): DiscountedProduct[] {
    const promotionProductsSortedByPrice = [...promotionProducts].sort(
      (a, b) => b.fullPrice - a.fullPrice
    )
    const productsWithPromotionApplied: DiscountedProduct[] = []
    let alreadyApplied = 0
    let paidProducts = 0
    for (let i = 0; i < promotionProducts.length; ++i) {
      if (!alreadyApplied) {
        const product = promotionProductsSortedByPrice[i]
        if (paidProducts + product.quantity > 1) {
          const discount = {
            discountType: '2x1' as const,
            discountAmount: this.promotion.allowRepeat
              ? Math.floor((product.quantity + paidProducts) / 2)
              : 1,
            discountConcept: this.promotion.name,
            promotionId: this.promotion.promotionId
          }
          productsWithPromotionApplied.push({ ...product, ...discount })
          alreadyApplied = this.promotion.allowRepeat ? 0 : 1
        } else {
          productsWithPromotionApplied.push(product)
        }
        paidProducts = (paidProducts + product.quantity) % 2
      }
    }
    return productsWithPromotionApplied
  }
}

class PromotionPercentage {
  promotion: Promotion
  constructor(promotion: Promotion) {
    this.promotion = promotion
  }
  applyPromotionIntoProducts(promotionProducts: DiscountedProduct[]): DiscountedProduct[] {
    return promotionProducts.map(promotionProduct => {
      return {
        ...promotionProduct,
        discountType: 'percentage' as const,
        discountAmount: this.promotion.discountAmount!,
        discountConcept: this.promotion.name,
        promotionId: this.promotion.promotionId
      }
    })
  }
}
